import useIsInteractive from "@/lib/hooks/use-is-interactive";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps } from "react";
import { tv, type VariantProps } from "tailwind-variants";

const badgeVariants = tv({
  base: "inline-flex items-center justify-center border text-xs leading-none w-fit whitespace-nowrap shrink-0 has-icon has-icon-size-4 overflow-hidden data-interactive:interactive data-interactive:cursor-pointer",
  variants: {
    variant: {
      default:
        "border-transparent bg-background-primary text-foreground-secondary data-interactive:hover:bg-background-neutral-hovered data-interactive:hover:text-foreground data-interactive:active:bg-background-primary data-interactive:active:text-foreground-secondary",
      blue: "border-blue-900 bg-blue-900 text-white",
      secondary:
        "border-transparent bg-background-neutral-hovered text-foreground data-interactive:hover:bg-background-neutral-pressed data-interactive:active:bg-background-neutral-disabled",
      outline:
        "border-border bg-background text-foreground-pressed data-interactive:hover:bg-background-neutral data-interactive:active:bg-background-neutral-hovered",
      critical:
        "border-transparent bg-background-critical text-foreground-on-critical data-interactive:hover:bg-background-critical-hovered data-interactive:active:bg-background-critical-pressed data-interactive:focus-visible:ring-border-critical/40 data-interactive:focus-visible:outline-border-critical",
      verified:
        "border-transparent bg-primary-500 text-foreground data-interactive:hover:bg-primary-600 data-interactive:active:bg-primary-700",
      background:
        "border-transparent bg-background-accent text-foreground data-interactive:hover:opacity-hovered data-interactive:active:opacity-pressed",
    },
    radius: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      full: "rounded-full",
    },
    size: {
      sm: "px-1 py-0.5 h-4 gap-0.5 text-[10px]",
      md: "px-2 py-0.5 h-6 gap-1",
      lg: "px-3 py-1 h-8 gap-2",
    },
  },
  defaultVariants: {
    variant: "default",
    radius: "md",
    size: "md",
  },
});

type BadgeProps = ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean };

export default function Badge({
  size,
  radius,
  asChild = false,
  variant,
  children,
  className,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";
  const isInteractive = useIsInteractive({ asChild, children });

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, radius, size }), className)}
      data-interactive={isInteractive ? "true" : undefined}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function IconBadge({ className, ...props }: BadgeProps) {
  return <Badge className={cn("min-w-6 px-0.5", className)} {...props} />;
}
